import React from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';

/**
 * <MetricRow />
 *
 * Hero metrics for the top of a governance page. Linear-style: small label,
 * large tabular value, optional delta pill, optional sparkline placeholder,
 * optional href to drill in.
 *
 * Props:
 *   metrics: Array<{
 *     key, label, value, delta?, deltaLabel?, tone?, hint?, icon?, href?, onClick?
 *   }>
 *   tone: 'default' | 'success' | 'warning' | 'danger' | 'accent' | 'muted'
 */
const TONE_CLASSES = {
  default: 'gov-text-primary',
  success: 'text-emerald-400',
  warning: 'text-amber-400',
  danger:  'text-rose-400',
  accent:  'text-indigo-400',
  muted:   'gov-text-tertiary',
};

function DeltaPill({ delta, deltaLabel }) {
  if (delta === undefined || delta === null) return null;
  const num = Number(delta) || 0;
  const Icon = num > 0 ? ArrowUp : num < 0 ? ArrowDown : Minus;
  const cls = num > 0
    ? 'text-emerald-400 bg-emerald-500/10'
    : num < 0
      ? 'text-rose-400 bg-rose-500/10'
      : 'text-slate-400 bg-slate-500/10';
  return (
    <span
      className={`gov-tabular inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ${cls}`}
      data-testid="gov-metric-delta"
    >
      <Icon className="h-3 w-3" />
      {Math.abs(num)}{typeof delta === 'string' && delta.endsWith('%') ? '%' : ''}
      {deltaLabel && <span className="opacity-75">{deltaLabel}</span>}
    </span>
  );
}

function MetricCell({ metric }) {
  const Icon = metric.icon;
  const toneCls = TONE_CLASSES[metric.tone] || TONE_CLASSES.default;
  const Wrapper = (metric.href || metric.onClick) ? 'button' : 'div';

  return (
    <Wrapper
      type={Wrapper === 'button' ? 'button' : undefined}
      onClick={metric.onClick}
      className={`gov-card group relative flex flex-col gap-2 p-5 text-left transition-colors hover:border-[var(--gov-border-strong)] ${(metric.href || metric.onClick) ? 'cursor-pointer' : ''}`}
      data-testid={`gov-metric-${metric.key}`}
    >
      <div className="flex items-center justify-between">
        <span className="gov-label">{metric.label}</span>
        {Icon && <Icon className="gov-text-tertiary h-4 w-4" />}
      </div>
      <div className="flex items-baseline gap-2">
        <span className={`gov-tabular gov-tick text-2xl font-semibold ${toneCls}`} data-testid={`gov-metric-${metric.key}-value`}>
          {metric.value}
        </span>
        <DeltaPill delta={metric.delta} deltaLabel={metric.deltaLabel} />
      </div>
      {metric.hint && (
        <p className="gov-text-tertiary text-xs">{metric.hint}</p>
      )}
    </Wrapper>
  );
}

export default function MetricRow({ metrics = [], cols }) {
  if (!metrics || metrics.length === 0) return null;
  const gridCols = cols || Math.min(metrics.length, 4);
  // Tailwind needs full class names — use a small map
  const colCls = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-2 lg:grid-cols-5',
    6: 'grid-cols-2 lg:grid-cols-6',
  }[gridCols] || 'grid-cols-2 lg:grid-cols-4';

  return (
    <div className={`grid gap-3 ${colCls}`} data-testid="gov-metric-row">
      {metrics.map((m) => (
        <MetricCell key={m.key} metric={m} />
      ))}
    </div>
  );
}
